<template>
  <div class="timer">
    {{ formattedTime }}
    <img
      alt="Clock Icon"
      :src="getStaticAssetUrl(`/resources/icons/clock.svg`)"
      loading="lazy"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { getStaticAssetUrl } from '@/helpers/cdnHelper'

interface Props {
  endDate: Date
}

const props = defineProps<Props>()

const secondsLeft = ref(0)
let intervalId: ReturnType<typeof setInterval> | null = null

const formattedTime = computed(() => {
  const total = Math.max(secondsLeft.value, 0)
  const minutes = String(Math.floor(total / 60)).padStart(2, "0")
  const seconds = String(total % 60).padStart(2, "0")
  return `${minutes}:${seconds}`
})

const updateSecondsLeft = () => {
  const now = Date.now()
  const target = new Date(props.endDate).getTime()
  secondsLeft.value = Math.max(Math.ceil((target - now) / 1000), 0)
}

const startTimer = () => {
  stopTimer()
  updateSecondsLeft()

  intervalId = setInterval(() => {
    updateSecondsLeft()

    if (secondsLeft.value <= 0) {
      stopTimer()
    }
  }, 1000)
}

const stopTimer = () => {
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
  }
}

watch(
  () => props.endDate,
  (newVal) => {
    if (newVal == null) {
      stopTimer()
      secondsLeft.value = 0
      return
    }
    startTimer()
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  stopTimer()
})
</script>

<style lang="scss" scoped>
.timer {
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
